/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { normalizeName } from '../../utilities/normalizeName';
import { FormContext, IFieldValidation, IFormField } from '../eve-form/context/FormContext';

import { IEveFormInputData } from './eve-form-input.data';
import { fieldwidth, IEveFormInputProps } from './eve-form-input.props.autogenerated';

export interface IEveFormInputViewProps extends IEveFormInputPropsExtended {
    registeredField: IFormField;
    handleChange: () => {};
    numCharsRemaining: number;
}

export interface IEveFormInputPropsExtended extends IEveFormInputProps<IEveFormInputData> {}

/**
 *
 * EveFormInput component. A standard text input field.
 * @extends {React.PureComponent<IEveFormInputPropsExtended>}
 */
class EveFormInput extends React.PureComponent<IEveFormInputProps<IEveFormInputData>> {
    public static contextType = FormContext;
    public declare context: React.ContextType<typeof FormContext>;
    private numCharsRemaining: number;

    constructor(props: IEveFormInputProps<IEveFormInputData>) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.reset = this.reset.bind(this);
        this.numCharsRemaining = props.config.characterlimit ?? 0;
    }

    /**
     * resets this field.
     * @returns void
     */
    public reset() {
        const registeredField = this.context.fields[this.props.config.name ? normalizeName(this.props.config.name) : ''];
        registeredField.value = '';
        registeredField.errors = [];
    }

    /**
     * Fires when this component has mounted; prepares validation and registers this field to context.
     * @returns void
     */
    public componentDidMount() {
        const {
            config: { validation, name, friendlyName },
            resources: { defaultError }
        } = this.props;

        const validationArray: IFieldValidation[] = [];

        if (validation) {
            const validationObject: IFieldValidation = {
                regex: validation?.regex ? validation.regex : '',
                errorMessage: validation?.errorMessage ? validation.errorMessage : defaultError,
                isRequiredValidation: validation?.isRequiredValidation ? validation.isRequiredValidation : false
            };

            validationArray.push(validationObject);
        }

        this.context.registerField({
            isRequired: validation?.isRequiredValidation ? validation.isRequiredValidation : false,
            name: name ? name : '',
            validation: validationArray,
            value: '',
            errors: [],
            friendly: friendlyName ? friendlyName : '',
            reset: this.reset
        });
    }

    /**
     * Updates this fields value in context.
     * @param e : React.ChangeEvent<HTMLInputElement>
     */
    private handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (this.props.config.characterlimit) {
            this.numCharsRemaining = this.props.config.characterlimit - e.target.value.length;
        }
        this.context.updateField(
            this.props.config.name ? this.props.config.name : '',
            e.target.value,
            this.props.config.friendlyName ? this.props.config.friendlyName : ''
        );
    }

    /**
     * Returns bootstrap styling based on enum value from module config. This is replicated in all eve-form input modules due to autogenerated definitions.
     * @returns string
     */
    private getWidth(): string {
        switch (this.props.config.fieldwidth) {
            case fieldwidth.halfWidth:
                return 'col-6';
            case fieldwidth.thirdWidth:
                return 'col-4';
            case fieldwidth.twoThirdWidth:
                return 'col-8';
            case fieldwidth.fullWidth:
            default:
                return 'col-12';
        }
    }

    /**
     * This renders the module.
     * @returns JSX.Element | Null
     */
    public render(): JSX.Element | null {
        const registeredField = this.context.fields[this.props.config.name ? normalizeName(this.props.config.name) : ''];
        if (!registeredField) {
            return null;
        }
        return (
            <div className={`eve-form__input ${this.getWidth()}`}>
                {this.props.renderView({
                    ...this.props,
                    registeredField: registeredField,
                    handleChange: this.handleChange,
                    numCharsRemaining: this.numCharsRemaining
                })}
            </div>
        );
    }
}

export default EveFormInput;
